import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveIcon from "@mui/icons-material/RemoveCircleOutline";
import InfoIcon from "@mui/icons-material/Info";
import { Link } from "react-router-dom";
import { toolLinks, toolFeatures } from "../Constants/tools_details";

const renderAccess = (value, planName) => {
  // If the plan is not free, show CheckIcon with "Available" if true, or with passed string tooltip if false
  if (planName !== "free") {
    return (
      <Tooltip title={value === true ? "Available" : value} arrow>
        <CheckIcon color="success" />
      </Tooltip>
    );
  }

  // If plan is free, check if it's true or false
  if (value) {
    // If value is a string, show CheckIcon with the string as the tooltip
    if (typeof value === "string") {
      return (
        <Tooltip title={value} arrow>
          <RemoveIcon sx={{ color: "#fbc02d" }} />
        </Tooltip>
      );
    }

    // If value is boolean true, show CheckIcon with "Available"
    return (
      <Tooltip title="Available" arrow>
        <CheckIcon color="success" />
      </Tooltip>
    );
  } else {
    // If false, show RemoveIcon with the reason for unavailability
    return (
      <Tooltip title={value || "Unavailable"} arrow>
        <RemoveIcon sx={{ color: "#fbc02d" }} />
      </Tooltip>
    );
  }
};

const renderCredits = (credits, tooltip) => {
  const tooltipText =
    tooltip ||
    (credits > 0
      ? `${credits} credit${credits > 1 ? "s" : ""} required`
      : "Free to use");

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <span>{credits > 0 ? credits : "Free"}</span>
      {tooltip && (
        <Tooltip title={tooltipText} arrow>
          <span style={{ marginLeft: 8 }}>
            <InfoIcon sx={{ fontSize: 16, color: "#1976d2" }} />
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

const FeatureTable = () => {
  return (
    <TableContainer component={Paper} sx={{ mt: 10, overflowX: "auto" }}>
      <Table>
        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
          <TableRow>
            <TableCell>
              <strong>Tool</strong>
            </TableCell>
            <TableCell>
              <strong>Description</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Free</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Starter</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Growth</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Pro</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Business</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Credits</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {toolFeatures.map((tool) => (
            <TableRow key={tool.name}>
              <TableCell>
                {toolLinks[tool.name] ? (
                  <Link
                    to={toolLinks[tool.name]}
                    style={{ color: "#1976d2", textDecoration: "none" }}
                  >
                    {tool.name}
                  </Link>
                ) : (
                  tool.name
                )}
              </TableCell>
              <TableCell>{tool.description}</TableCell>
              {["free", "starter", "growth", "pro", "business"].map((plan) => (
                <TableCell align="center" key={plan}>
                  {renderAccess(tool.plans?.[plan], plan)}
                </TableCell>
              ))}
              <TableCell align="center">
                {renderCredits(tool.credits, tool.creditsTooltip)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const PricingDetailsSections = () => {
  return (
    <Box
      sx={{
        mx: { xs: 2, md: 15 },
        mt: 8,
        mb: 7,
      }}
    >
      <Typography variant="h4" align="center" gutterBottom>
        Detailed plans comparison
      </Typography>
      <Typography
        variant="body1"
        align="center"
        color="text.secondary"
        sx={{ mb: 4 }}
      >
        Understand which tools are included in each plan and how many credits
        they consume.
      </Typography>
      <FeatureTable />
    </Box>
  );
};

export default PricingDetailsSections;
